/**
 * @fileoverview KPI strip: live system health at a glance.
 *
 * @module HydraulicBalancingTesting/components/KPIDashboard
 */

import React from 'react';
import { Tooltip, Progress, Tag } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import { HEAT_PUMP } from '../constants';
import { formatSimTime, joulesToKWh } from '../hooks';

import styles from '../HydraulicBalancingTesting.module.css';

const copColor = (cop) => (cop >= 3.5 ? '#52c41a' : cop >= 2.8 ? '#faad14' : '#ff4d4f');

const Kpi = ({ label, value, sub, tooltip, accent }) => (
  <Tooltip title={tooltip}>
    <div className={styles.kpiCard}>
      <div className={styles.kpiLabel}>{label}</div>
      <div className={styles.kpiValue} style={accent ? { color: accent } : undefined}>{value}</div>
      {sub && <div className={styles.kpiSub}>{sub}</div>}
    </div>
  </Tooltip>
);

export const KPIDashboard = ({ engine, onOpenScoresInfo }) => {
  const { derived, params, energy, simTime, timeSinceChange } = engine;
  const { tSupply, tReturn, cop, totalFlowM3h, network, scores, spf, alarms, totalQrad } = derived;
  const spread = tSupply - tReturn;
  // Instantaneous power: heat delivered now (thermal) and the electricity it draws.
  const thermalKW = totalQrad / 1000;
  const electricKW = (totalQrad / Math.max(cop, 0.1) + network.pumpPower) / 1000;

  return (
    <div className={styles.kpiStrip}>
      <Kpi
        label="Supply / Return"
        value={`${tSupply.toFixed(1)} / ${tReturn.toFixed(1)} °C`}
        sub={`spread ${spread.toFixed(1)} °C`}
        tooltip="Heat-pump supply and mixed return temperature. Heat-pump systems target a small spread (≈5 K) — but a vanishing spread means overflow."
      />
      <Kpi
        label="COP"
        value={cop.toFixed(2)}
        sub={params.supplyMode === 'curve' ? 'weather-compensated' : 'fixed supply'}
        accent={copColor(cop)}
        tooltip="Coefficient of performance. Every Kelvin less supply temperature is worth ≈2–3% COP."
      />
      <Kpi
        label="Total flow"
        value={`${totalFlowM3h.toFixed(2)} m³/h`}
        sub={
          alarms.minFlow
            ? <Tag color="error" style={{ margin: 0 }}>below min {HEAT_PUMP.MIN_FLOW_M3H}</Tag>
            : `min ${HEAT_PUMP.MIN_FLOW_M3H} m³/h`
        }
        tooltip="Circulation through the heat pump. Below the minimum flow the heat pump trips."
      />
      <Kpi
        label="Pump"
        value={`${network.pumpPower.toFixed(0)} W`}
        sub={`head ${params.pumpHead.toFixed(0)} kPa`}
        tooltip="Electrical pump power ≈ V̇·Δp/η. An oversized head wastes energy around the clock."
      />
      <Kpi
        label="Energy"
        value={`${joulesToKWh(energy.thermalJ)} kWh`}
        sub={`SPF ${spf > 0 ? spf.toFixed(2) : '—'} · el. ${joulesToKWh(energy.electricJ)} kWh`}
        tooltip="Heat delivered since scenario start, electricity consumed (heat pump + circulation pump), and the resulting seasonal performance factor."
      />
      <Kpi
        label="Power"
        value={`${thermalKW.toFixed(2)} kW`}
        sub={`el. ${electricKW.toFixed(2)} kW`}
        tooltip="Instantaneous power right now: thermal output (sum of all radiators) and the electrical input it draws (heat pump compressor + circulation pump). Energy is the running total of this over time."
      />
      <Kpi
        label="Sim clock"
        value={formatSimTime(simTime)}
        sub={`settled ${formatSimTime(timeSinceChange)}`}
        tooltip="Simulated time, and time since you last changed a setting (missions require a settled system)."
      />
      <Tooltip title="Balance: flow deviations vs design (commissioning view) · Comfort: rooms at setpoint · Efficiency: COP + pump power">
        <div className={styles.kpiCard} style={{ minWidth: 170 }}>
          <div className={styles.kpiLabel} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Scores</span>
            <InfoCircleOutlined
              onClick={(e) => { e.stopPropagation(); onOpenScoresInfo?.(); }}
              style={{ color: '#1890ff', cursor: 'pointer' }}
              title="How are these scores calculated?"
            />
          </div>
          <div className={styles.scoreRow}>
            <span>Balance</span>
            <Progress percent={scores.balance} size="small" showInfo={false} strokeColor="#1b9e77" style={{ width: 70, margin: 0 }} />
            <b>{scores.balance}</b>
          </div>
          <div className={styles.scoreRow}>
            <span>Comfort</span>
            <Progress percent={scores.comfort} size="small" showInfo={false} strokeColor="#1890ff" style={{ width: 70, margin: 0 }} />
            <b>{scores.comfort}</b>
          </div>
          <div className={styles.scoreRow}>
            <span>Efficiency</span>
            <Progress percent={scores.efficiency} size="small" showInfo={false} strokeColor="#faad14" style={{ width: 70, margin: 0 }} />
            <b>{scores.efficiency}</b>
          </div>
        </div>
      </Tooltip>
    </div>
  );
};
